import { MessageContext } from '@snapchat/snap-design-system-marketing';
import { unique } from 'radashi';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { initialCartState, useCart } from '../Cart/hooks';
import type {
  ContentfulShopifyProduct,
  ContentfulShopifyProductsResponse,
  ProductAvailability,
  ShopifyProduct,
  ShopifyProviderProps,
  ShopifyState,
  ShopifyVariant,
} from '../Cart/types';
import { Feature, useFeatureFlags } from '../FeatureFlags';
import { contentfulProductsQuery } from './contentfulProductsQuery';

export const ShopifyContext = createContext<ShopifyState>({
  products: {
    shopify: {},
    contentful: {},
    extras: {
      bundleItems: [],
      accessories: [],
    },
    variantMap: {},
  },
  cart: initialCartState,
});

export const ShopifyProvider: FC<PropsWithChildren<ShopifyProviderProps>> = ({
  children,
  shopifyData,
}) => {
  const {
    userLocation: { country },
  } = useContext(AppContext);
  const { formatMessage } = useContext(MessageContext);
  const featureFlags = useFeatureFlags();
  const products = {
    shopify: {},
    contentful: {},
    extras: {},
    variantMap: {} as Record<string, { handle: string; variant: ShopifyVariant }>,
  };
  const cart = useCart();

  const { data: contentfulProductData } = useContentfulQuery<ContentfulShopifyProductsResponse>(
    contentfulProductsQuery,
    {
      skip: !Config.shopify,
      ssr: true,
    }
  );

  const calculateAtp = useCallback(
    ({ available, atp = 0 }: ProductAvailability): string | null => {
      if (atp === 0 && available) {
        return formatMessage({
          id: 'shopifyATPTodayFreeShipping',
          defaultMessage: 'Ships for free today',
        });
      }

      if (atp > 0 && available) {
        if (atp < 7) {
          if (atp === 1) {
            return formatMessage({
              id: 'shopifyATPDayFreeShipping',
              defaultMessage: 'Ships for free in 1 day',
            });
          }

          return formatMessage(
            { id: 'shopifyATPDaysFreeShipping', defaultMessage: 'Ships for free in {days} days' },
            { days: String(atp) }
          );
        }
        const bufferRemainder = atp % 7;
        const weeks = (atp - bufferRemainder) / 7;
        const weekRange = `${weeks}-${weeks + 1}`;

        if (weeks === 1) {
          return formatMessage({
            id: 'shopifyATPWeekFreeShipping',
            defaultMessage: 'Ships for free in 1 week',
          });
        }

        return formatMessage(
          {
            id: 'shopifyATPWeeksFreeShipping',
            defaultMessage: 'Ships for free in {weeks} weeks',
          },
          { weeks: weekRange }
        );
      }

      return null;
    },
    [formatMessage]
  );

  if (!Config.shopify) {
    return <>{children}</>;
  }

  if (shopifyData) {
    products.shopify = shopifyData.products.edges.reduce((acc, { node }) => {
      // TODO: this was changes to incorporate multiple vairants but we may need another pass
      // on this logic. Especially for the prices / instock status
      const firstVariant = node.variants.edges[0]!;

      const variants: ShopifyVariant[] = node.variants.edges.map(({ node: v }) => ({
        id: v.id,
        title: v.title,
        sku: v.sku,
        price: Number(v.price.amount ?? '0'),
        available: v.availableForSale,
        options: v.selectedOptions.reduce((opts, { name, value }) => {
          opts[name] = value;
          return opts;
        }, {} as Record<string, string>),
      }));

      const product: ShopifyProduct = {
        image: node.featuredImage?.url,
        price: Number(node.priceRange.maxVariantPrice.amount ?? '0'),
        available: node.availableForSale,
        id: firstVariant.node.id,
        nodeId: node.id,
        title: node.title,
        handle: node.handle,
        sku: firstVariant.node.sku,
        variants,
      };

      const atpDays = firstVariant.node.metafields.find(field => field?.key === 'ATPBufferDays');
      const maxQuantity = firstVariant.node.metafields.find(field => field?.key === 'maxQuantity');

      if (atpDays) {
        product.atpMessage = calculateAtp({
          available: product.available,
          atp: Number.parseInt(atpDays.value),
        });
      }

      if (maxQuantity) {
        product.maxQuantity = Number.parseInt(maxQuantity.value);
      }

      // Build variantMap for reverse lookup (variantId → handle + variant)
      for (const variant of variants) {
        products.variantMap[variant.id] = { handle: node.handle, variant };
      }

      acc[node.handle] = product;
      return acc;
    }, {} as Record<string, ShopifyProduct>);
  }

  if (contentfulProductData) {
    products.contentful = contentfulProductData.shopifyProductCollection.items.reduce(
      (acc: Record<string, ContentfulShopifyProduct>, item) => {
        acc[item.productHandle] = item;
        return acc;
      },
      {}
    );

    const contentfulProductsFromCart = Object.keys(cart.lineItems).map(
      lineItemId => (products.contentful as Record<string, ContentfulShopifyProduct>)[lineItemId]
    );

    products.extras = {
      bundleItems: unique(
        contentfulProductsFromCart.flatMap(product =>
          (product?.bundleItemsCollection?.items ?? []).map(({ productHandle }) => productHandle)
        )
      ),
      accessories: unique(
        contentfulProductsFromCart.flatMap(product =>
          (product?.accessoriesCollection?.items ?? []).map(({ productHandle }) => productHandle)
        )
      ),
    };
  }

  const enableEuropeanStores = featureFlags[Feature.ENABLE_PIXY_EUROPEAN_STORES] === 'true';

  const isHiddenBehindFeatureFlag = !enableEuropeanStores && (country === 'GB' || country === 'FR');
  const shopifyStore = Config.shopify?.stores[country];
  const store =
    shopifyStore && !isHiddenBehindFeatureFlag ? shopifyStore[Config.deploymentType] : undefined;

  return (
    <ShopifyContext.Provider
      value={{
        store,
        products,
        cart,
        currencyCode: shopifyData?.shop.paymentSettings.currencyCode,
      }}
    >
      {children}
    </ShopifyContext.Provider>
  );
};
